import {
  processSeoDataForRegistry,
  replaceTemplateVariables,
} from "@/lib/seoHelpers";
import { SeoScriptRegistry } from "@/lib/seoScriptRegistry";
import { LenisProvider } from "@/providers/LenisProvider";
import "@/styles/globals.css";
import "lenis/dist/lenis.css";
import { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { retrackWhatConverts } from "@/lib/whatconverts";
import RootLayout from "./layout";
import RootLayoutSSR from "./layoutSSR";

const DEFAULT_BASE_URL = "https://www.solomonplasticsurgery.com";

const normalizeBaseUrl = (url: string): string =>
  (url || DEFAULT_BASE_URL).replace(/\/+$/, "");

const normalizePathForCanonical = (path: string): string => {
  const [withoutHash] = path.split("#");
  const [withoutQuery] = withoutHash.split("?");
  let normalized = withoutQuery.trim();

  if (!normalized) normalized = "/";
  if (!normalized.startsWith("/")) normalized = `/${normalized}`;
  normalized = normalized.replace(/\/{2,}/g, "/");
  if (normalized !== "/" && !normalized.endsWith("/")) normalized += "/";

  return normalized;
};

const resolveCanonicalUrl = (baseUrl: string, path: string): string =>
  `${normalizeBaseUrl(baseUrl)}${normalizePathForCanonical(path)}`;

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  // WhatConverts only swaps numbers / freezes lead attribution on hard load.
  // Re-run the swap ourselves on each in-app navigation (the native pass is
  // one-shot). Not fired on initial load — Next only emits this for SPA navs,
  // which is exactly when the native swap does not run.
  useEffect(() => {
    router.events.on("routeChangeComplete", retrackWhatConverts);
    return () => {
      router.events.off("routeChangeComplete", retrackWhatConverts);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process SEO data on the server side
  if (typeof window === "undefined") {
    // Always reset registry between requests
    if (pageProps.page?.seo) {
      processSeoDataForRegistry(
        pageProps.page.seo,
        pageProps.page,
        pageProps.page.version === "staging"
      );
    }
  }

  // Get the default page title from page data
  const defaultTitle = pageProps.page?.title || "";
  const isStaging = pageProps.page?.version === "staging";
  const isForApproval = (
    pageProps.page?.status === "forapproval" ||
    pageProps.page?.patient?.status === "forapproval" ||
    pageProps.page?.procedure?.status === "forapproval"
  );
  const baseUrl = normalizeBaseUrl(
    process.env.NEXT_PUBLIC_BASE_URL || DEFAULT_BASE_URL
  );
  const routePath =
    router.asPath || (pageProps.page?.slug ? `/${pageProps.page.slug}` : "/");
  let canonicalUrl = resolveCanonicalUrl(baseUrl, routePath);

  // Initialize SEO values
  let metaTitle = isStaging
    ? "(Staging Version) " + defaultTitle
    : defaultTitle;
  let metaDescription = "";

  // Extract SEO values from entries if available
  if (pageProps.page?.seo && Array.isArray(pageProps.page.seo)) {
    for (const seoItem of pageProps.page.seo) {
      if (
        !seoItem.seo_collection_id?.entries ||
        !Array.isArray(seoItem.seo_collection_id?.entries)
      ) {
        continue;
      }

      // Search through all entries for both title and description
      for (const entry of seoItem.seo_collection_id.entries) {
        if (!entry.seo_entries_id) continue;

        // Handle SEO title
        if (
          entry.seo_entries_id.type === "title" ||
          entry.seo_entries_id.type === "page_title"
        ) {
          let titleContent = entry.seo_entries_id.entry || "";
          // Replace template variables with actual values
          titleContent = replaceTemplateVariables(
            titleContent,
            pageProps.page,
            pageProps.page?.seo
          );
          metaTitle = isStaging
            ? "(Staging Version) " + titleContent
            : titleContent;
        }

        // Handle SEO description
        if (entry.seo_entries_id.type === "description") {
          metaDescription = entry.seo_entries_id.entry || "";
          // Replace template variables with actual values
          metaDescription = replaceTemplateVariables(
            metaDescription,
            pageProps.page,
            pageProps.page?.seo
          );
        }

        // Handle canonical URL override from SEO entries
        if (entry.seo_entries_id.type === "canonical") {
          const canonicalValue = replaceTemplateVariables(
            entry.seo_entries_id.entry || "",
            pageProps.page,
            pageProps.page?.seo
          ).trim();

          if (canonicalValue) {
            canonicalUrl =
              canonicalValue.startsWith("http://") ||
              canonicalValue.startsWith("https://")
                ? canonicalValue
                : resolveCanonicalUrl(baseUrl, canonicalValue);
          }
        }
      }
    }
  }

  return (
    <>
      <Head>
        {metaTitle && <title>{metaTitle}</title>}
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        {isForApproval && (
          <meta name="robots" content="noindex, nofollow" />
        )}
      </Head>

      {/* Client-side scripts using Next.js Script component */}
      <SeoScriptRegistry.Client />

      <LenisProvider>
        {pageProps.layoutData ? (
          <RootLayoutSSR layoutData={pageProps.layoutData}>
            <Component {...pageProps} />
          </RootLayoutSSR>
        ) : (
          <RootLayout>
            <Component {...pageProps} />
          </RootLayout>
        )}
      </LenisProvider>
    </>
  );
}

export default App;
